import { HierarchyAccessError, object, request } from "./api";
import type { TranscriptSelection } from "./transcript";

export interface RunRepository { repositoryId: string; mountAlias: string; mode: "read" | "write"; revision: string }
export interface RepositoryResource { id: string; locator: string; defaultBranch?: string }
export interface RepositoryContext {
  accountId: string; workspaceId: string; threadId: string; headNodeId?: string;
  source: "new" | "stateless" | "snapshot" | "imported";
  repositories: { repositoryId: string; mountAlias: string; mode: string; revision: string }[];
}
export interface RepositoryChoices { context: RepositoryContext; repositories: RepositoryResource[] }

function identifier(value: unknown): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9_-]{1,128}$/.test(value)) throw new HierarchyAccessError();
  return value;
}
export function pinnedRevision(value: string): boolean { return /^(?:[a-f0-9]{40}|[a-f0-9]{64})$/.test(value); }

export function repositoryRequests(raw: unknown): RunRepository[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw) || raw.length > 32) throw new HierarchyAccessError();
  const rows = raw.map((item): RunRepository => {
    const row = object(item), { mode, revision } = row;
    if (Object.keys(row).some((key) => !["repositoryId", "mountAlias", "mode", "revision"].includes(key)) ||
      (mode !== "read" && mode !== "write") || typeof revision !== "string" || !revision.trim() || new TextEncoder().encode(revision).length > 512 || revision.includes("\0")) throw new HierarchyAccessError();
    return { repositoryId: identifier(row.repositoryId), mountAlias: identifier(row.mountAlias), mode, revision };
  });
  if (new Set(rows.map((row) => row.repositoryId)).size !== rows.length || new Set(rows.map((row) => row.mountAlias)).size !== rows.length || rows.filter((row) => row.mode === "write").length > 1) throw new HierarchyAccessError();
  return rows;
}

export function sameRepositories(left: unknown, right: unknown): boolean {
  const sorted = (value: unknown) => repositoryRequests(value).sort((a, b) => a.repositoryId.localeCompare(b.repositoryId));
  return JSON.stringify(sorted(left)) === JSON.stringify(sorted(right));
}

export function verifiedBindings(raw: unknown, requests: RunRepository[]): RunRepository[] {
  repositoryRequests(requests);
  if (raw !== undefined && raw !== null && (!Array.isArray(raw) || raw.length > 32)) throw new HierarchyAccessError();
  const rows = repositoryRequests((raw ?? []).map((item: unknown) => {
    const row = object(item);
    return { repositoryId: row.repositoryId, mountAlias: row.mountAlias, mode: row.mode, revision: row.baseRevision };
  }));
  if (rows.length !== requests.length || rows.some((row) => !pinnedRevision(row.revision))) throw new HierarchyAccessError();
  return requests.map((expected) => {
    const actual = rows.find((row) => row.repositoryId === expected.repositoryId);
    if (!actual || actual.mountAlias !== expected.mountAlias || actual.mode !== expected.mode || (pinnedRevision(expected.revision) && actual.revision !== expected.revision)) throw new HierarchyAccessError();
    return actual;
  });
}

export function repositoryContext(raw: unknown, selection: TranscriptSelection, current: Record<string, unknown>): RepositoryContext {
  const row = object(raw);
  const headNodeId = row.headNodeId === undefined || row.headNodeId === "" ? undefined : identifier(row.headNodeId);
  if (row.accountId !== selection.accountId || row.workspaceId !== selection.workspaceId || row.threadId !== selection.threadId || headNodeId !== (current.headNodeId || undefined) || !Array.isArray(row.repositories)) throw new HierarchyAccessError();
  const scope = { accountId: selection.accountId, workspaceId: selection.workspaceId, threadId: selection.threadId, headNodeId };
  if (row.source === "imported") {
    if (!headNodeId || row.repositories.length !== 1) throw new HierarchyAccessError();
    const source = object(row.repositories[0]);
    if (source.mode !== "" || source.mountAlias !== "" || typeof source.revision !== "string" || !pinnedRevision(source.revision)) throw new HierarchyAccessError();
    return { ...scope, source: "imported", repositories: [{ repositoryId: identifier(source.repositoryId), mountAlias: "", mode: "", revision: source.revision }] };
  }
  const repositories = repositoryRequests(row.repositories);
  switch (row.source) {
    case "new": if (headNodeId || repositories.length) throw new HierarchyAccessError(); break;
    case "stateless": if (!headNodeId || repositories.length) throw new HierarchyAccessError(); break;
    case "snapshot": if (!headNodeId || !repositories.length || repositories.some((repo) => !pinnedRevision(repo.revision))) throw new HierarchyAccessError(); break;
    default: throw new HierarchyAccessError();
  }
  return { ...scope, source: row.source, repositories };
}

export function acceptsRepositories(context: RepositoryContext, raw: unknown): boolean {
  const selected = repositoryRequests(raw);
  if (context.source === "snapshot") return sameRepositories(context.repositories, selected);
  if (context.source === "imported") return selected.length === 1 && selected[0].repositoryId === context.repositories[0].repositoryId && selected[0].revision === context.repositories[0].revision;
  return true;
}

export async function loadRepositoryContext(selection: TranscriptSelection, current: Record<string, unknown>, signal?: AbortSignal): Promise<RepositoryContext> {
  return repositoryContext(await request(`/code/workspaces/${encodeURIComponent(selection.workspaceId)}/threads/${encodeURIComponent(selection.threadId)}/repository-context`, signal), selection, current);
}

export async function loadRepositoryChoices(selection: TranscriptSelection, current: Record<string, unknown>, signal?: AbortSignal): Promise<RepositoryChoices> {
  const context = await loadRepositoryContext(selection, current, signal);
  const page = await request(`/code/workspaces/${encodeURIComponent(selection.workspaceId)}/repositories`, signal);
  if (page.accountId !== selection.accountId || page.workspaceId !== selection.workspaceId || !Array.isArray(page.repositories) || page.repositories.length > 1000) throw new HierarchyAccessError();
  const repositories = page.repositories.map((item): RepositoryResource => {
    const row = object(item);
    if (row.accountId !== selection.accountId || row.workspaceId !== selection.workspaceId || typeof row.locator !== "string" || !row.locator || (row.defaultBranch !== undefined && typeof row.defaultBranch !== "string")) throw new HierarchyAccessError();
    return { id: identifier(row.id), locator: row.locator, defaultBranch: row.defaultBranch as string | undefined };
  });
  const ids = new Set(repositories.map((row) => row.id));
  if (ids.size !== repositories.length || context.repositories.some((row) => !ids.has(row.repositoryId))) throw new HierarchyAccessError();
  return { context, repositories };
}
